import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, forwardRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { FormulaMonacoEditorComponent } from '../formula-monaco-editor/formula-monaco-editor.component';
import {
  FixedViewFieldGuiRow,
  buildFixedViewSchema,
  buildIndexAppViewSchema,
  parseFixedViewSchema,
  parseIndexAppViewSchema,
  safeParseJsonObject,
} from '../../utils/view-schema.utils';

@Component({
  selector: 'app-view-schema-editor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormulaMonacoEditorComponent],
  templateUrl: './view-schema-editor.component.html',
  styleUrl: './view-schema-editor.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ViewSchemaEditorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ViewSchemaEditorComponent),
      multi: true,
    },
  ],
})
export class ViewSchemaEditorComponent implements ControlValueAccessor, Validator {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  @Input() mode: 'indexApp' | 'fixed' = 'indexApp';
  @Input() fieldKeys: string[] = [];

  protected advancedOpen = false;
  protected advancedJsonError: string | null = null;

  private isWritingValue = false;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};
  private rowId = 0;

  protected readonly indexAppForm = this.fb.group({
    fields: this.fb.array([]),
    where: [''],
    filters: this.fb.array([]),
    groupBy: [''],
    orderBy: [''],
    skip: [null as number | null],
    take: [null as number | null],
    advancedJson: [''],
  });

  protected readonly fixedForm = this.fb.group({
    fields: this.fb.array([]),
    advancedJson: [''],
  });

  constructor() {
    this.indexAppForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.emitValue());
    this.fixedForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.emitValue());
  }

  get indexAppFields(): FormArray {
    return this.indexAppForm.get('fields') as FormArray;
  }

  get indexAppFilters(): FormArray {
    return this.indexAppForm.get('filters') as FormArray;
  }

  get fixedFields(): FormArray {
    return this.fixedForm.get('fields') as FormArray;
  }

  writeValue(value: string | null): void {
    this.isWritingValue = true;
    this.advancedJsonError = null;
    this.advancedOpen = false;

    try {
      const parsed = safeParseJsonObject(value);
      if (this.mode === 'fixed') {
        this.applyFixedValue(parsed);
      } else {
        this.applyIndexAppValue(parsed);
      }
    } catch {
      this.advancedJsonError = 'View Schema JSON を読み込めませんでした。形式を確認してください。';
      if (this.mode === 'fixed') {
        this.applyFixedValue(null);
      } else {
        this.applyIndexAppValue(null);
      }
    } finally {
      this.isWritingValue = false;
      this.onValidatorChange();
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    const options = { emitEvent: false };
    if (isDisabled) {
      this.indexAppForm.disable(options);
      this.fixedForm.disable(options);
    } else {
      this.indexAppForm.enable(options);
      this.fixedForm.enable(options);
    }
  }

  validate(_: AbstractControl): ValidationErrors | null {
    return this.advancedJsonError ? { advancedJsonInvalid: true } : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  protected addIndexAppField(field?: { fieldName?: string; formula?: string; value?: string }): void {
    this.indexAppFields.push(
      this.fb.group({
        _id: [this.rowId++],
        fieldName: [field?.fieldName ?? '', Validators.required],
        formula: [field?.formula ?? ''],
        value: [field?.value ?? ''],
      }),
    );
  }

  protected removeIndexAppField(index: number): void {
    this.indexAppFields.removeAt(index);
    this.emitTouch();
  }

  protected moveIndexAppFieldUp(index: number): void {
    this.moveRow(this.indexAppFields, index, index - 1);
  }

  protected moveIndexAppFieldDown(index: number): void {
    this.moveRow(this.indexAppFields, index, index + 1);
  }

  protected addFilter(filter?: { fieldName?: string; expression?: string; operand?: string }): void {
    this.indexAppFilters.push(
      this.fb.group({
        _id: [this.rowId++],
        fieldName: [filter?.fieldName ?? '', Validators.required],
        expression: [filter?.expression ?? '==', Validators.required],
        operand: [filter?.operand ?? ''],
      }),
    );
  }

  protected removeFilter(index: number): void {
    this.indexAppFilters.removeAt(index);
    this.emitTouch();
  }

  protected addFixedField(field?: FixedViewFieldGuiRow): void {
    this.fixedFields.push(
      this.fb.group({
        _id: [this.rowId++],
        kind: [field?.kind ?? 'field', Validators.required],
        fieldName: [field?.fieldName ?? '', Validators.required],
        formula: [field?.formula ?? ''],
      }),
    );
  }

  protected removeFixedField(index: number): void {
    this.fixedFields.removeAt(index);
    this.emitTouch();
  }

  protected moveFixedFieldUp(index: number): void {
    this.moveRow(this.fixedFields, index, index - 1);
  }

  protected moveFixedFieldDown(index: number): void {
    this.moveRow(this.fixedFields, index, index + 1);
  }

  protected toggleAdvanced(): void {
    this.advancedOpen = !this.advancedOpen;
    this.emitTouch();
  }

  protected handleBlur(): void {
    this.emitTouch();
  }

  private moveRow(formArray: FormArray, fromIndex: number, toIndex: number): void {
    if (toIndex < 0 || toIndex >= formArray.length || fromIndex === toIndex) return;
    const row = formArray.at(fromIndex);
    formArray.removeAt(fromIndex);
    formArray.insert(toIndex, row);
    this.emitTouch();
  }

  private applyIndexAppValue(parsed: Record<string, unknown> | null): void {
    const slice = parseIndexAppViewSchema(parsed);

    this.clearFormArray(this.indexAppFields);
    slice.fields.forEach((field) => this.addIndexAppField(field));
    this.clearFormArray(this.indexAppFilters);
    slice.filters.forEach((filter) => this.addFilter(filter));

    this.indexAppForm.patchValue(
      {
        where: slice.where,
        groupBy: slice.groupBy,
        orderBy: slice.orderBy,
        skip: slice.skip,
        take: slice.take,
        advancedJson: slice.advancedJson,
      },
      { emitEvent: false },
    );
    this.advancedOpen = slice.advancedJson.length > 0;
  }

  private applyFixedValue(parsed: Record<string, unknown> | null): void {
    const slice = parseFixedViewSchema(parsed);
    this.clearFormArray(this.fixedFields);
    slice.fields.forEach((field) => this.addFixedField(field));
    this.fixedForm.patchValue({ advancedJson: slice.advancedJson }, { emitEvent: false });
    this.advancedOpen = slice.advancedJson.length > 0;
  }

  private clearFormArray(formArray: FormArray): void {
    while (formArray.length > 0) {
      formArray.removeAt(0);
    }
  }

  private emitValue(): void {
    if (this.isWritingValue) return;

    const payload = this.buildValue();
    if (payload === null) return;

    this.onChange(payload);
    this.onValidatorChange();
  }

  private emitTouch(): void {
    this.onTouched();
  }

  private buildValue(): string | null {
    try {
      if (this.mode === 'fixed') {
        const advanced = safeParseJsonObject(this.fixedForm.get('advancedJson')?.value);
        const rawFields = this.fixedFields.getRawValue() as FixedViewFieldGuiRow[];
        const schema = buildFixedViewSchema({ fields: rawFields }, advanced);
        this.advancedJsonError = null;
        return schema ? JSON.stringify(schema, null, 2) : '';
      }

      const advanced = safeParseJsonObject(this.indexAppForm.get('advancedJson')?.value);
      const rawValue = this.indexAppForm.getRawValue() as {
        fields: { fieldName: string; formula?: string; value?: string }[];
        where: string;
        filters: { fieldName: string; expression: string; operand: string }[];
        groupBy: string;
        orderBy: string;
        skip: number | null;
        take: number | null;
      };
      const schema = buildIndexAppViewSchema(
        {
          fields: rawValue.fields,
          where: rawValue.where,
          filters: rawValue.filters,
          groupBy: rawValue.groupBy,
          orderBy: rawValue.orderBy,
          skip: rawValue.skip,
          take: rawValue.take,
        },
        advanced,
      );
      this.advancedJsonError = null;
      return schema ? JSON.stringify(schema, null, 2) : '';
    } catch {
      this.advancedJsonError = '追加 JSON の形式が正しくありません。';
      return null;
    }
  }
}
